import { getStore } from '@/store/index';
import { Component, Vue, Watch, Ref } from 'vue-property-decorator';
import { Getter } from 'vuex-class';

import { getComponentProps } from '@/utils/vueUtils';
import { SM } from '@/utils/breakpointUtils';
import SizeBreakpoints from '@/common/interfaces/SizeBreakpoints';

import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import { TeamSearchBlockViewModel } from './TeamDirectorySearchModule.types';
import SearchResultsCategory from '@/modules/SearchModule/SearchModule.types';
import LargeSearchBar from '@/components/LargeSearchBar/LargeSearchBar.vue';
import SearchResultsList from '@/components/SearchResultsList/SearchResultsList.vue';
import SearchPreviewCard from '@/components/SearchPreviewCard/SearchPreviewCard.vue';
import SearchSuggestions from '@/components/SearchSuggestions/SearchSuggestions.vue';

import IconArrow from '@/assets/icons/arrow.svg?inline';
import registerSubModule from '@/modules/TeamExpeditionFinderModule/TeamExpeditionFinderModule.store';
import { SearchHints } from '../NavigationModule/NavigationModule.api';
import debounce from 'lodash/debounce';

const SearchModuleProps = Vue.extend({
  props: getComponentProps<TeamSearchBlockViewModel>({
    Destinations: [
      {
        Text: 'Alaska',
        Value: 1,
      },
      {
        Text: 'Amazon',
        Value: 2,
      },
      {
        Text: 'Antarctic',
        Value: 3,
      },
    ],
    SearchSuggestions: {
      Title: 'Suggested Searches',
      //Placeholder: 'Search by name, expertise or destination…', //TODO: move placeholder to TeamSearchBlockViewModel?
      Items: [
        {
          Title: 'By Expertise',
          Items: [
            'Captains',
            'Undersea Diving',
            'National Geographic Photographers',
            'Naturalists',
          ],
        },
        {
          Title: 'By Destination',
          Items: ['Antarctica', 'Galapagos'],
        },
      ],
    },
  }),
});

@Component({
  components: {
    LargeSearchBar,
    SearchResultsList,
    SearchPreviewCard,
    SearchSuggestions,
    TeamExpeditionFinderModule: () =>
      import('@/modules/TeamExpeditionFinderModule/TeamExpeditionFinderModule.vue'),
    IconArrow,
  },
})
export default class SearchModule extends SearchModuleProps {
  tempScrollPos = 0;

  modalIsActive = false;
  searchHasFocus = false;
  showFinder = !!this.p.DestinationId;

  pushSearchValue = null;
  isValidSearchQuery = false;
  searchResults = [];
  debouncedHandleSearch = debounce(this.handleSearchQueryValidation, 500);

  // DUMMY DATA FOR THE EXPEDITION FINDER MODULE
  TeamFinderTitle = 'Find the team aboard your chosen expedition';

  async created() {
    (this.$options as any).modulePath = await registerSubModule(getStore(), {});
  }

  setModalActive(setVal: boolean) {
    let teamfinder = <HTMLElement>this.$refs.teamfinder;
    let searchResults = <HTMLElement>this.$refs.results;
    let searchSuggestions = (this.$refs.suggestions as any).$el;

    if (setVal === true && this.modalIsActive === false) {
      this.tempScrollPos = teamfinder.getBoundingClientRect().top - document.documentElement.getBoundingClientRect().top - 60;

      document.documentElement.classList.add('no-scroll', 'h-100');
      document.body.classList.add('no-scroll', 'h-100');
      document.getElementById('app').classList.add('h-100');

      this.modalIsActive = true;
      setTimeout(() => {
        window.scrollTo(0, 0)
      }, 100)
    } else if (setVal === false && this.modalIsActive === true) {
      document.documentElement.classList.remove('no-scroll', 'h-100');
      document.body.classList.remove('no-scroll', 'h-100');
      document.getElementById('app').classList.remove('h-100');

      window.scrollTo(0, Math.max(window.pageYOffset, this.tempScrollPos));
      this.modalIsActive = false;
    }
  }

  handleSearchFocus(isFocus: boolean, e: Event) {
    if (e.type === 'blur' && window.innerWidth <= SM) return;
    this.searchHasFocus = this.isValidSearchQuery ? true : isFocus;
    if (window.innerWidth <= SM) this.setModalActive(isFocus);
    this.pushSearchValue = this.isValidSearchQuery ? this.pushSearchValue : null;
    this.showFinder = false;

    if (document.querySelector('.is-search-active')) {
      this.searchHasFocus = true;
    }
  }

  handleSearchInput(input) {
    const oldVal = this.pushSearchValue;
    this.pushSearchValue = input;
    if (this.pushSearchValue === oldVal) {
      this.handleSearchQueryValidation(this.pushSearchValue, oldVal);
    }
  }

  handleSuggestionSelected(data) {
    this.pushSearchValue = data;
  }

  finderToggle(value) {
    if (this.showFinder !== value) {
      if (window.innerWidth <= SM) this.setModalActive(value);
      this.showFinder = value;
    }
  }

  @Getter isInBreakpoint!: (breakpoints: SizeBreakpoints[] | SizeBreakpoints) => boolean;
  get isMobileWidth() {
    return this.isInBreakpoint(['xs', 'sm', 'md']);
  }

  @Watch('pushSearchValue')
  some(newVal: string, oldVal: string) {
    this.debouncedHandleSearch(newVal, oldVal);
  }

  async handleSearchQueryValidation(newVal: string, oldVal: string) {
    this.isValidSearchQuery = newVal && newVal.length > 2 ? true : false;
    if (this.isValidSearchQuery) {
      //API call
      this.pushSearchValue = newVal;
      this.searchResults = await SearchHints(newVal, true, SearchResultsCategory.Team);
      if (!this.searchResults) this.searchResults = [];
    } else {
      this.searchResults = [];
    }
  }

  handleValidSearch(value: string) {
    SearchHints(value, true, SearchResultsCategory.Team).then(
      (response) => (this.searchResults = response),
    );
  }

  clearSearch() {
    this.searchHasFocus = false;
    this.pushSearchValue = null;
    if (window.innerWidth <= SM) this.setModalActive(false);
  }

  mounted() {
    if (document.querySelector('.is-search-active')) {
      this.searchHasFocus = true;
    }
  }
}
